import React, { useState, useEffect } from 'react';
import { Bell, X, Check, User, Mail, Calendar, MessageSquare, Users, Radio } from 'lucide-react';
import API_URL from '../config/ApiUrl';

export const NotificationBell = ({ userId }) => {
  const [notifications, setNotifications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch notifications WITHOUT marking as read
  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications?limit=20&newest_first=true`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications);
        // Use the unread_count from the backend
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // Mark all notifications as read
  const markNotificationsAsRead = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/mark-read`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`Marked ${data.marked_read} notifications as read`);
        
        // Update local state - mark all as read
        setNotifications(prev => 
          prev.map(n => ({ ...n, read: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (userId) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  // Mark notifications as read when user opens the modal
  useEffect(() => {
    if (showModal && unreadCount > 0) {
      markNotificationsAsRead();
    }
  }, [showModal]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch(type) {
      case 'message':
      case 'sesh_message':
        return <MessageSquare size={20} color="#2563eb" />;
      case 'follow':
      case 'friend_request':
        return <Users size={20} color="#059669" />;
      case 'stream':
      case 'live_stream':
        return <Radio size={20} color="#dc2626" />;
      default:
        return <Bell size={20} color="#6b7280" />;
    }
  };

  // Get background color based on notification type
  const getNotificationBgColor = (type) => {
    switch(type) {
      case 'message':
      case 'sesh_message':
        return '#dbeafe';
      case 'follow':
      case 'friend_request':
        return '#d1fae5';
      case 'stream':
      case 'live_stream':
        return '#fee2e2';
      default:
        return '#f3f4f6';
    }
  };

  return (
    <>
      {/* Notification Bell Button */}
      <button
        onClick={() => setShowModal(true)}
        style={{
          position: 'relative',
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '0.5rem',
          padding: '0.5rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
        }}
      >
        <Bell size={20} color="#ccc" />
        
        {/* Notification Badge */}
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: '#dc2626',
              color: 'white',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              borderRadius: '9999px',
              minWidth: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 0.25rem',
              border: '2px solid #111'
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: '#1a1a1a',
              borderRadius: '1rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.5), 0 8px 10px -6px rgb(0 0 0 / 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '1.5rem',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fff' }}>
                  Notifications
                </h2>
                <p style={{ fontSize: '0.875rem', color: '#888', marginTop: '0.25rem' }}>
                  {notifications.length} {notifications.length === 1 ? 'notification' : 'notifications'}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <X size={24} color="#888" />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1rem' }}>
              {notifications.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                  <Bell size={48} color="#444" style={{ margin: '0 auto 1rem' }} />
                  <p style={{ color: '#888', fontSize: '1rem' }}>
                    No notifications yet
                  </p>
                  <p style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    We'll notify you when something happens
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      style={{
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '0.75rem',
                        padding: '1rem',
                        background: notification.read ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 212, 170, 0.08)',
                        transition: 'all 0.2s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = notification.read 
                          ? 'rgba(255, 255, 255, 0.03)' 
                          : 'rgba(0, 212, 170, 0.08)';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                        {/* Notification Icon */}
                        <div
                          style={{
                            background: getNotificationBgColor(notification.type),
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {getNotificationIcon(notification.type)}
                        </div>
                        
                        {/* Notification Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ 
                            color: '#fff', 
                            fontSize: '0.9375rem',
                            lineHeight: '1.5',
                            marginBottom: '0.25rem'
                          }}>
                            {notification.content}
                          </p>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            marginTop: '0.5rem'
                          }}>
                            <Calendar size={12} color="#888" />
                            <span style={{ fontSize: '0.75rem', color: '#888' }}>
                              {notification.createdAt}
                              {/* {formatDate(notification.created_at)} */}
                            </span>
                            {!notification.read && (
                              <span style={{
                                background: '#00d4aa',
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                marginLeft: '0.25rem'
                              }} />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer - Show if there are more notifications */}
            {notifications.length >= 20 && (
              <div style={{
                padding: '1rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center'
              }}>
                <p style={{ color: '#888', fontSize: '0.875rem' }}>
                  Showing recent 20 notifications
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};